import { CheckboxCell, CheckboxHeader } from '@/components/table/DataTable';
import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import { Chip } from '@/components/ui/chip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/drodown-menu-custom';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  useGetAbstractScreeningByIdService,
  useUpdatePaperScreeningResultJustificationService,
  useUpdatePaperScreeningResultService,
  useUpdatePaperScreeningValidationStatusService,
} from '@/services/literatureReviews';
import { GetAbstractScreeningResultsResponse } from '@ditto/shared';
import { ColumnDef } from '@tanstack/react-table';
import {
  Ban,
  BookOpen,
  Check,
  ChevronDown,
  Circle,
  CircleHelp,
  ExternalLink,
  Pencil,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

export const useColumns = (
  id: string | undefined,
  setSelectedPaperScreening: (
    paperScreening:
      | GetAbstractScreeningResultsResponse['data'][number]
      | undefined
  ) => void,
  data: GetAbstractScreeningResultsResponse['data']
): ColumnDef<GetAbstractScreeningResultsResponse['data'][number]>[] => {
  const { abstractScreening } = useGetAbstractScreeningByIdService(id);
  const allCriteria = abstractScreening?.criteria ?? [];
  const columns: ColumnDef<
    GetAbstractScreeningResultsResponse['data'][number]
  >[] = useMemo(() => {
    return [
      {
        id: 'select',
        header: ({ table }) => {
          return <CheckboxHeader table={table} />;
        },
        cell: ({ row }) => {
          return <CheckboxCell row={row} />;
        },
        meta: {
          headerClassName: 'w-[50px] min-w-[50px] max-w-[50px]',
          cellClassName: 'w-[50px] min-w-[50px] max-w-[50px]',
        },
      },
      {
        id: 'work',
        meta: {
          headerClassName:
            'w-[350px] min-w-[350px] max-w-[350px] border-r border-gray-200',
          cellClassName:
            'w-[350px] min-w-[350px] max-w-[350px] h-[185px] align-top border-r border-gray-200',
        },
        header: () => (
          <Text
            variant="body-sm"
            color="primary"
            className="!text-neutral-900 !font-inter !text-sm !font-medium !leading-[150%] !tracking-[0.07px] !whitespace-nowrap"
          >
            Paper
          </Text>
        ),
        cell: ({ row }) => {
          const work = row.original.work;

          const handleClick = () => {
            setSelectedPaperScreening(row.original);
          };

          return (
            <FlexCol
              className="w-full h-full p-2 gap-2 cursor-pointer"
              alignItems="start"
              justifyContent="start"
              onClick={handleClick}
            >
              <Text variant="body-sm" color="primary">
                {work.name}
              </Text>
              <Ellipsis lineClamp={3} hasTooltip asChild>
                <Text variant="body-xs" color="tertiary">
                  {work.authors}
                </Text>
              </Ellipsis>
              <FlexRow
                alignItems="center"
                justifyContent="start"
                className="gap-2"
              >
                <BookOpen className="w-4 h-4 text-tertiary" />
                <Ellipsis lineClamp={2} hasTooltip asChild>
                  <Text variant="body-xs" color="tertiary" w="fit">
                    {work.journal},{' '}
                    {new Date(work.publicationDate ?? '').toLocaleDateString()}
                  </Text>
                </Ellipsis>
              </FlexRow>
              {work.doi && (
                <FlexRow
                  alignItems="center"
                  justifyContent="start"
                  className="gap-2"
                >
                  <Link
                    to={work.doi}
                    target="_blank"
                    onClick={e => e.stopPropagation()}
                  >
                    <FlexRow
                      alignItems="center"
                      justifyContent="start"
                      className="gap-2"
                    >
                      <Text variant="body-xs" className="text-blue-700">
                        View full article
                      </Text>
                      <ExternalLink className="w-4 h-4 text-blue-700" />
                    </FlexRow>
                  </Link>
                </FlexRow>
              )}
            </FlexCol>
          );
        },
      },
      {
        id: 'inclusionStatus',
        accessorKey: 'inclusionStatus',
        header: () => (
          <Text
            variant="body-sm"
            color="primary"
            className="!text-neutral-900 !font-inter !text-sm !font-medium !leading-[150%] !tracking-[0.07px] !whitespace-nowrap w-[280px]"
          >
            Screening Decision
          </Text>
        ),
        cell: ({ row }) => {
          const { updatePaperScreeningResult } =
            useUpdatePaperScreeningResultService();

          const { updatePaperScreeningValidationStatus } =
            useUpdatePaperScreeningValidationStatusService();

          const { updatePaperScreeningResultJustification } =
            useUpdatePaperScreeningResultJustificationService();

          const [status, setStatus] = useState<
            ('include' | 'exclude' | 'inconclusive') | undefined
          >(row.original.inclusionStatus);
          const [justification, setJustification] = useState<
            string | undefined
          >(row.original.justification);
          const [validationStatus, setValidationStatus] = useState<
            'not_validated' | 'validated'
          >(row.original.validationStatus);

          const handleUpdatePaperScreeningResult =
            (status: 'include' | 'exclude') => () => {
              if (validationStatus === 'validated') {
                return;
              }
              updatePaperScreeningResult({
                paperScreeningId: row.original.id,
                data: { screeningResult: status },
              });
              setStatus(status);
            };

          const handleUpdatePaperScreeningValidationStatus =
            (validationStatus: 'not_validated' | 'validated') => () => {
              updatePaperScreeningValidationStatus({
                paperScreeningId: row.original.id,
                data: { validationStatus },
              });
              setValidationStatus(validationStatus);
            };

          const handleUpdatePaperScreeningResultJustification = (
            e: ChangeEvent<HTMLTextAreaElement>
          ) => {
            setJustification(e.target.value);
          };

          const saveJustification = () => {
            if (!justification) {
              setJustification(row.original.justification);
              toast.error('Justification is required');
              return;
            }
            updatePaperScreeningResultJustification(
              {
                paperScreeningId: row.original.id,
                data: { justification },
              },
              {
                onSuccess: () => {
                  toast.success('Justification saved');
                },
                onError: () => {
                  setJustification(row.original.justification);
                  toast.error('Failed to save justification');
                },
              }
            );
          };

          const isGenerating = row.original.isGenerating;

          const hasData = row.original.criteriaResults.some(
            criteriaResult => criteriaResult.value !== undefined
          );

          useEffect(() => {
            setStatus(row.original.inclusionStatus);
          }, [row.original.inclusionStatus]);

          useEffect(() => {
            setJustification(row.original.justification);
          }, [row.original.justification]);

          useEffect(() => {
            setValidationStatus(row.original.validationStatus);
          }, [row.original.validationStatus]);

          const disabledValidation =
            status === 'inconclusive' ||
            status === undefined ||
            !justification?.trim();

          const disabledValidationReason =
            status === 'inconclusive'
              ? 'You cannot validate a paper screening if the status is inconclusive'
              : status === undefined
                ? 'You cannot validate a paper screening if the status is not set'
                : !justification?.trim()
                  ? 'You cannot validate a paper screening if the justification is not set'
                  : undefined;

          let background, textColor, iconColor, icon;

          if (status === 'include') {
            background = 'bg-green-50';
            textColor = 'text-green-800';
            iconColor = 'text-green-600';
            icon = <Check className="w-4 h-4" />;
          }
          if (status === 'exclude') {
            background = 'bg-red-100';
            textColor = 'text-red-800';
            iconColor = 'text-red-600';
            icon = <Ban className="w-4 h-4" />;
          }
          if (status === 'inconclusive') {
            background = 'bg-yellow-100';
            textColor = 'text-yellow-900';
            iconColor = 'text-yellow-700';
            icon = <CircleHelp className="w-4 h-4" />;
          }
          if (status === undefined) {
            background = 'bg-gray-100';
            textColor = 'text-gray-800';
            iconColor = 'text-gray-600';
          }

          if (isGenerating) {
            return (
              <FlexCol w="full" h="full" p="2">
                <Skeleton className="h-4" />
              </FlexCol>
            );
          }

          if (!hasData) {
            return null;
          }

          return (
            <FlexCol gap="4" justifyContent="between" className="px-2">
              <FlexRow className="w-full justify-between" alignItems="center">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    disabled={validationStatus === 'validated'}
                    className="w-fit"
                  >
                    <FlexRow
                      alignItems="center"
                      gap="2"
                      justifyContent="center"
                      className={cn(background, 'p-1.5 rounded-xl h-full ')}
                    >
                      {icon && <span className={iconColor}>{icon}</span>}
                      <Text
                        variant="body-sm"
                        className={`capitalize ${textColor}`}
                      >
                        {status ?? 'Set status'}
                      </Text>
                      {validationStatus === 'not_validated' && <ChevronDown />}
                    </FlexRow>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onClick={handleUpdatePaperScreeningResult('include')}
                    >
                      Include
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleUpdatePaperScreeningResult('exclude')}
                    >
                      Exclude
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {validationStatus === 'validated' ? (
                  <Button
                    variant="outline"
                    onClick={handleUpdatePaperScreeningValidationStatus(
                      'not_validated'
                    )}
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </Button>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Button
                          disabled={disabledValidation}
                          onClick={handleUpdatePaperScreeningValidationStatus(
                            'validated'
                          )}
                        >
                          <Check className="w-4 h-4" />
                          Validate
                        </Button>
                      </div>
                    </TooltipTrigger>
                    {disabledValidation && (
                      <TooltipContent>
                        {disabledValidationReason}
                      </TooltipContent>
                    )}
                  </Tooltip>
                )}
              </FlexRow>
              <FlexCol gap="1">
                <Text className="uppercase text-xs font-semibold text-neutral-600">
                  Justification:
                </Text>
                {validationStatus === 'validated' ? (
                  <Text variant="body-sm" color="primary">
                    {justification}
                  </Text>
                ) : (
                  <Textarea
                    value={justification}
                    onBlur={saveJustification}
                    onChange={handleUpdatePaperScreeningResultJustification}
                  />
                )}
              </FlexCol>
            </FlexCol>
          );
        },
        meta: {
          headerClassName:
            'w-[400px] min-w-[400px] max-w-[400px] border-r border-gray-200',
          cellClassName:
            'font-medium w-[400px] min-w-[400px] max-w-[400px] align-top border-r border-gray-200',
        },
      },
      {
        id: 'criteriaResults',
        accessorKey: 'criteriaResults',
        header: () => (
          <Text
            variant="body-sm"
            color="primary"
            className="!text-neutral-900 !font-inter !text-sm !font-medium !leading-[150%] !tracking-[0.07px] !whitespace-nowrap w-[400px]"
          >
            Criteria
          </Text>
        ),
        cell: ({ row }) => {
          const criteriaResults = row.original.criteriaResults;

          const handleClick = () => {
            setSelectedPaperScreening(row.original);
          };

          const getColor = (value: 'yes' | 'no' | 'not_found') => {
            if (value === 'yes') {
              return 'fill-red-500 text-red-500';
            }
            if (value === 'no') {
              return 'fill-green-500 text-green-500';
            }
            return 'fill-yellow-500 text-yellow-500';
          };

          const isGenerating = row.original.isGenerating;

          if (isGenerating) {
            return (
              <FlexCol w="full" h="full" p="2">
                <Skeleton className="h-4" />
              </FlexCol>
            );
          }

          return (
            <Box
              className="gap-2 flex flex-wrap p-2 cursor-pointer"
              onClick={handleClick}
            >
              {criteriaResults.length > 0 &&
                allCriteria.map(criteria => (
                  <Chip size="sm" key={criteria.id} interactive={false}>
                    <Circle
                      className={cn(
                        'w-2 h-2',
                        getColor(
                          criteriaResults.find(
                            cr => cr.criteriaId === criteria.id
                          )?.value ?? 'not_found'
                        )
                      )}
                    />
                    <Text variant="body-xs" color="primary">
                      {criteria.name}
                    </Text>
                  </Chip>
                ))}
            </Box>
          );
        },
        meta: {
          headerClassName:
            'w-[400px] min-w-[400px] max-w-[400px] border-r border-gray-200',
          cellClassName:
            'w-[400px] min-w-[400px] max-w-[400px] align-top border-r border-gray-200',
        },
      },
    ];
  }, [abstractScreening, setSelectedPaperScreening, data]);

  return columns;
};
